import { TouchEvent, useRef, useState } from "react";
import { AnimateOnScroll } from "../../../components/animate";
import { AnimateWordsOnScroll } from "../../../components/animate/animateWords";
import { ScribbleReveal } from "../../../components/animate/scribbleReveal";
import { Icon } from "../../../components/icon/icon";
import { comparisonTableRows, scribblePaths } from "../data/landingContent";

const TIKKET_WORDMARK = "/assets/images/landing-tikket-wordmark.svg";

export const ComparisonTableSection = () => {
  return (
    <section className="max-width-website py-12 md:py-16">
      <div className="mx-auto mb-10 flex max-w-[600px] flex-col items-center gap-3 text-center md:mb-16">
        <AnimateWordsOnScroll
          as="h2"
          className="relative font-dela text-[28px] leading-[1.15] text-ink md:text-[44px]"
        >
          Made for event{" "}
          <span className="relative inline-block">
            creators
            <ScribbleReveal
              {...scribblePaths.hero}
              className="pointer-events-none absolute left-1/2 top-1/2 -z-10 w-[170px] -translate-x-1/2 -translate-y-1/2 md:w-[260px]"
            />
          </span>
        </AnimateWordsOnScroll>
        <AnimateOnScroll animation="blur-up" delay={0.4}>
          <p className="font-onest text-lg tracking-[-0.4px] text-[#4f4e4e]">
            Tikket was built for event creators at every stage of their lives.
          </p>
        </AnimateOnScroll>
      </div>

      <AnimateOnScroll
        animation="fade-up"
        delay={0.15}
        className="mx-auto hidden max-w-[960px] md:flex md:items-start"
      >
        <div className="flex flex-1 flex-col">
          <div className="flex h-[70px] items-center" />
          {comparisonTableRows.map((row, idx) => (
            <div
              key={`label-${row}-${idx}`}
              className="flex h-[76px] items-center border-t border-black/[0.08]"
            >
              <p className="font-onest text-xl font-medium tracking-[-0.4px] text-ink">
                {row}
              </p>
            </div>
          ))}
        </div>

        <div className="flex w-[220px] shrink-0 flex-col rounded-[12px] bg-[#DFF3F9]">
          <div className="flex h-[70px] items-center justify-center">
            <img
              src={TIKKET_WORDMARK}
              alt="Tikket"
              className="h-[18px] w-auto"
            />
          </div>
          {comparisonTableRows.map((row, idx) => (
            <div
              key={`tikket-${row}-${idx}`}
              className="flex h-[76px] items-center justify-center border-t border-black/[0.08]"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#327AF3] text-[#F3F3F4]">
                <Icon svg="check-plain" width={14} height={11} />
              </span>
            </div>
          ))}
        </div>

        <div className="flex w-[220px] shrink-0 flex-col">
          <div className="flex h-[70px] items-center justify-center">
            <p className="text-center font-onest text-xl tracking-[-0.4px] text-ink">
              Other platforms
            </p>
          </div>
          {comparisonTableRows.map((row, idx) => (
            <div
              key={`other-${row}-${idx}`}
              className="flex h-[76px] items-center justify-center border-t border-black/[0.08]"
            >
              <Icon
                svg="circle-close"
                width={32}
                height={32}
                className="text-grey-400"
              />
            </div>
          ))}
        </div>
      </AnimateOnScroll>

      <ComparisonCardCarousel />
    </section>
  );
};

const ComparisonCardCarousel = () => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goTo = (next: number) => {
    setIndex((next + comparisonTableRows.length) % comparisonTableRows.length);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      goTo(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <AnimateOnScroll
      animation="fade-up"
      delay={0.15}
      className="mx-auto flex max-w-[420px] flex-col items-center gap-5 md:hidden"
    >
      <div
        className="flex min-h-[188px] w-full flex-col justify-between gap-8 rounded-[16px] bg-white/75 p-5 shadow-[0px_14px_40px_rgba(9,21,37,0.08)] ring-1 ring-grey-950/10 backdrop-blur-xl"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <p className="font-onest text-[24px] font-bold leading-[29px] text-ink">
          {comparisonTableRows[index]}
        </p>
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#327AF3] text-[#F3F3F4]">
              <Icon svg="check-plain" width={12} height={10} />
            </span>
            <img
              src={TIKKET_WORDMARK}
              alt="Tikket"
              className="h-[18px] w-auto"
            />
          </div>
          <div className="flex items-center gap-2.5">
            <Icon
              svg="circle-close"
              width={28}
              height={28}
              className="text-grey-400"
            />
            <p className="font-onest text-xl tracking-[-0.4px] text-ink">
              Other platforms
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-1.5">
        {comparisonTableRows.map((_, dotIdx) => (
          <button
            key={dotIdx}
            type="button"
            aria-label={`Go to slide ${dotIdx + 1}`}
            onClick={() => goTo(dotIdx)}
            className={`h-2 w-2 rounded-full transition-colors ${
              dotIdx === index ? "bg-[#327AF3]" : "bg-grey-200"
            }`}
          />
        ))}
      </div>
    </AnimateOnScroll>
  );
};
